import { inject, Injectable } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { ComponentType } from '@angular/cdk/portal';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

export interface OpenDialogOptions<D = unknown> {
  data?: D;
  /** When true, overlay-click and Esc do NOT close the dialog. Default: false. */
  disableClose?: boolean;
  /** Fixed dialog width, applied via CSS so mobile rules can override it. Default: content-sized. */
  width?: string;
  /** Extra panel classes, appended to `csd-dialog-panel`. */
  panelClass?: string | string[];
  /** Per-dialog backdrop overrides for the `--csd-dialog-overlay-*` variables. */
  overlayBackground?: string;
  overlayBlur?: string;
  /**
   * How the dialog presents below the mobile breakpoint. 'modal' (default) keeps
   * the centered card at near-full width; 'page' fills the viewport borderless.
   */
  mobilePresentation?: 'page' | 'modal';
}

/**
 * Thin wrapper over MatDialog that standardizes overlay/backdrop styling and
 * a11y defaults so callers don't repeat MatDialogConfig.
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  private readonly matDialog = inject(MatDialog);
  private readonly overlayContainer = inject(OverlayContainer);

  /** Distinguishes concurrent dialogs so their per-dialog styles never collide. */
  private instanceCount = 0;

  /** Dialogs that have started closing (still in `openDialogs` during their exit). */
  private readonly closing = new WeakSet<MatDialogRef<unknown>>();

  open<T, D = unknown, R = unknown>(
    component: ComponentType<T>,
    options: OpenDialogOptions<D> = {},
  ): MatDialogRef<T, R> {
    // A unique marker class per open, so we can target THIS dialog's own pane and
    // backdrop below — earlier code set the CSS variables on the shared overlay
    // container, where a second dialog (or its close) clobbered the first's width
    // and backdrop. Scoping to each dialog's own elements needs no cleanup: they
    // are removed with the dialog.
    this.instanceCount += 1;
    const uid = `csd-dialog-${this.instanceCount}`;
    const titleId = `${uid}-title`;

    const panelClass = [
      'csd-dialog-panel',
      ...(options.mobilePresentation === 'page' ? ['csd-dialog-panel--page'] : []),
      uid,
      ...toArray(options.panelClass),
    ];

    // One dialog active at a time (design): any dialog already open is hidden — not
    // closed, so its state survives — while this one is up (see showTopOnly).
    const stacked = this.matDialog.openDialogs.some((open) => !this.closing.has(open));

    const config: MatDialogConfig<D> = {
      data: options.data,
      disableClose: options.disableClose ?? false,
      // Neutralize MatDialog's inline 80vw cap; the `.csd-dialog-panel` classes own the width.
      maxWidth: '100vw',
      panelClass,
      // Over another dialog, the backdrop below already dims the page; this one stays
      // clear (still catching clicks) so the page never gets a second layer.
      backdropClass: [
        'csd-dialog-backdrop',
        `${uid}-backdrop`,
        ...(stacked ? [DIALOG_BACKDROP_CLEAR_CLASS] : []),
      ],
      // Names the dialog by its own heading (WCAG 4.1.2). Our dialogs render their title
      // as an h1/h2 rather than `mat-dialog-title` (whose padding/typography would change
      // the design), so Material binds aria-labelledby to this id from creation — before
      // the container takes focus — and the heading is given the id below.
      ariaLabelledBy: titleId,
      // Focus the dialog container on open (no visible ring; screen readers announce the
      // dialog by its title). The title is the first Tab stop, so the keyboard journey
      // starts on it when the user presses Tab.
      autoFocus: 'dialog',
      restoreFocus: true,
    };

    const ref = this.matDialog.open<T, D, R>(component, config);
    this.showTopOnly();
    // Re-shown as this one starts closing — before its exit animation ends and Material
    // restores focus into the dialog below.
    ref.beforeClosed().subscribe(() => {
      this.closing.add(ref);
      this.showTopOnly();
    });

    // Sizing and backdrop are driven through CSS variables (not MatDialog's inline
    // styles) so stylesheet rules — including the mobile media queries — can
    // override them without `!important`. The overlay is attached synchronously by
    // open(), so the pane/backdrop for this dialog exist now.
    const container = this.overlayContainer.getContainerElement();
    const pane = container.querySelector<HTMLElement>(`.${uid}`);
    const backdrop = container.querySelector<HTMLElement>(`.${uid}-backdrop`);
    if (options.width) {
      pane?.style.setProperty('--csd-panel-width', options.width);
    }
    // A stacked dialog's backdrop stays clear (the class above): an inline override would
    // beat it and dim the page a second time.
    if (options.overlayBackground && !stacked) {
      backdrop?.style.setProperty('--csd-dialog-overlay-bg', options.overlayBackground);
    }
    if (options.overlayBlur && !stacked) {
      backdrop?.style.setProperty('--csd-dialog-overlay-blur', options.overlayBlur);
    }
    // The content view is created by open(), so its (static) heading exists now.
    const heading = pane?.querySelector<HTMLElement>('h1, h2');
    if (heading) {
      heading.id = titleId;
    }
    // A heading that binds its own id (e.g. the full-screen filter's `[id]="titleId"`)
    // replaces ours on first render — re-point the dialog's name at the id it ends up
    // with, so aria-labelledby never references a missing element.
    ref.afterOpened().subscribe(() => {
      const rendered = pane?.querySelector<HTMLElement>('h1, h2');
      const container = pane?.querySelector<HTMLElement>('.mat-mdc-dialog-container');
      if (rendered?.id && rendered.id !== titleId) {
        container?.setAttribute('aria-labelledby', rendered.id);
      }
    });

    return ref;
  }

  /** Shows only the topmost dialog that isn't closing and hides the rest — from the
   *  whole stack each time, so closing any dialog (even one in the middle) never leaves
   *  two showing. */
  private showTopOnly(): void {
    const open = this.matDialog.openDialogs.filter((dialog) => !this.closing.has(dialog));
    const top = open.at(-1);
    open.forEach((dialog) =>
      dialog === top
        ? dialog.removePanelClass(DIALOG_BEHIND_CLASS)
        : dialog.addPanelClass(DIALOG_BEHIND_CLASS),
    );
  }
}

/** On a dialog's pane while another dialog is open on top of it (see `open`). */
export const DIALOG_BEHIND_CLASS = 'csd-dialog-panel--behind';

/** On a dialog's backdrop when it opens over another dialog. */
export const DIALOG_BACKDROP_CLEAR_CLASS = 'csd-dialog-backdrop--clear';

function toArray(value: string | string[] | undefined): string[] {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}
